/********************************************************************************
 * Copyright (c) 2021 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import JSG from '@cedalo/jsg-ui';
import { UpdateCustomInfosCommand } from './commands';
import { getCustomInfos, pinInfo, sizeInfo, splitterInfo } from './custominfos';

const isMinimizeMaximizeButton = (item) => {
	const name = item && item.getName().getValue();
	return name && (name === 'maximize' || name === 'minimize');
};
const isStreamSheetContainer = (item) => item && item instanceof JSG.StreamSheetContainer;
const getParentStreamSheetContainer = (item) =>
	// eslint-disable-next-line no-nested-ternary
	isStreamSheetContainer(item) ? item : item != null ? getParentStreamSheetContainer(item.getParent()) : item;

const getItemFromCommand = (cmd) => {
	cmd = (cmd && cmd.commands) ? cmd.commands.find((c) => c.getItem != null) : cmd;
	return cmd && cmd.getItem ? cmd.getItem() : undefined;
};
const getMessageContainer = (cmd) => {
	const container = getItemFromCommand(cmd);
	const type = container && container.getItemType ? container.getItemType() : undefined;
	if (type) {
		if (type === 'inboxcontainer' || type === 'outboxcontainer') {
			return container;
		} else if (type === 'treenode') {
			return container.getParent();
		}
	}

	return type && (type === 'inboxcontainer' || type === 'outboxcontainer') ? container : undefined;
};
const getStreamSheetContainer = (cmd) => {
	const item = getItemFromCommand(cmd);
	return isStreamSheetContainer(item) ? item : undefined;
};

const pinInfos = getCustomInfos(pinInfo());
const splitterInfos = getCustomInfos(splitterInfo());
const bboxInfos = getCustomInfos(pinInfo(), sizeInfo());

const wrapCreateCommand = (getInfos, getItem) => (createCmd, scope) => (...args) => {
	const cmd = createCmd.apply(scope, args);
	const item = getItem(cmd);
	return item ? new UpdateCustomInfosCommand(item, cmd, getInfos) : cmd;
};
// define wrappers for interaction commands:
const wrapCreateMoveCommand = wrapCreateCommand(pinInfos, getStreamSheetContainer);
const wrapCreateResizeCommand = wrapCreateCommand(bboxInfos, getStreamSheetContainer);
const wrapCreateSplitterCommand = wrapCreateCommand(splitterInfos, getMessageContainer);

export class MoveActivator extends JSG.MoveActivator {
	activateInteraction(interaction, dispatcher) {
		const delegate = interaction._delegate;
		delegate.createCommand = wrapCreateMoveCommand(delegate.createCommand, delegate);
		return super.activateInteraction(interaction, dispatcher);
	}
}
export class ResizeActivator extends JSG.ResizeActivator {
	activateInteraction(interaction, dispatcher) {
		interaction.createCommand = wrapCreateResizeCommand(interaction.createCommand, interaction);
		return super.activateInteraction(interaction, dispatcher);
	}
}
export class SplitterActivator extends JSG.SplitterActivator {
	activateInteraction(interaction, dispatcher) {
		interaction.createCommand = wrapCreateSplitterCommand(interaction.createCommand, interaction);
		return super.activateInteraction(interaction, dispatcher);
	}
}
export class ButtonActivator extends JSG.ButtonActivator {
	onMouseUp(event, viewer, dispatcher) {
		const btn = this._controller && this._controller.getModel();
		const stsContainer = getParentStreamSheetContainer(btn);
		super.onMouseUp(event, viewer, dispatcher);
		// send custom info command to server and do nothing on client...
		if (isMinimizeMaximizeButton(btn) && stsContainer) {
			const cmd = new UpdateCustomInfosCommand(stsContainer, new JSG.Command(), bboxInfos);
			viewer.getInteractionHandler().execute(cmd);
		}
	}
}
